import { getDictionary } from "../../i18n/dictionary";
import type { LinkedChild } from "./data";

interface ChildRowProps {
  lc: LinkedChild;
  dict: ReturnType<typeof getDictionary>;
  onClick?: () => void;
}

export function ChildRow({ lc, dict, onClick }: ChildRowProps) {
  const tc = dict.service.children;
  const { child, link, linkState, ownerEmail } = lc;
  const isClickable = linkState === "active" && !!onClick;

  const bg =
    linkState === "active"
      ? "var(--surface)"
      : linkState === "pending"
        ? "var(--peach-tint)"
        : "var(--surface-sunk)";

  const borderColor =
    linkState === "active"
      ? "var(--line)"
      : linkState === "pending"
        ? "var(--peach)"
        : "var(--line-strong)";

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  }

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={
        isClickable ? `${child.displayLabel} — view detail` : undefined
      }
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 18px",
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: "var(--r-lg)",
        cursor: isClickable ? "pointer" : "default",
        gap: 12,
        minHeight: 68,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          minWidth: 0,
          flex: 1,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            flex: "none",
            background:
              linkState === "revoked" ? "var(--surface-sunk)" : "var(--brand-tint)",
            display: "grid",
            placeItems: "center",
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 16,
            color: linkState === "revoked" ? "var(--ink-faint)" : "var(--brand)",
          }}
        >
          {child.displayLabel.charAt(0)}
        </span>
        <div style={{ minWidth: 0 }}>
          <b
            style={{
              display: "block",
              fontSize: 15.5,
              color:
                linkState === "revoked" ? "var(--ink-faint)" : "var(--ink-deep)",
              fontFamily: "var(--font-body)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {child.displayLabel}
          </b>
          {linkState === "active" && link.relation === "owner" && (
            <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
              {tc.ownerTag}
            </span>
          )}
          {linkState === "active" && link.relation === "viewer" && ownerEmail && (
            <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
              {tc.viewerTag} {ownerEmail}
            </span>
          )}
          {linkState === "pending" && (
            <span style={{ fontSize: 13, color: "var(--peach-deep)" }}>
              {tc.pendingTag}
            </span>
          )}
          {linkState === "revoked" && (
            <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
              {tc.revokedTag}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
